import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { toast } from 'sonner';

const Login = () => {
  const { user, loading, login, register, resendVerification } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', phone: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F7F5F0]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2C4A3B]"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setUnverifiedEmail(null);
    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password, rememberMe);
        if (!result.success) {
          if (result.unverified) setUnverifiedEmail(formData.email);
          toast.error(result.error);
        }
      } else {
        if (!/^\+?\d[\d\s-]{6,}$/.test(formData.phone.trim())) {
          toast.error('Please enter a valid phone number'); return;
        }
        const result = await register(formData.email, formData.password, formData.name, formData.phone);
        if (result.success) {
          setRegisterSuccess({ email: formData.email, emailSent: result.emailSent });
          setFormData({ email: '', password: '', name: '', phone: '' });
          toast.success(result.message);
        } else {
          toast.error(result.error);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async (email) => {
    const r = await resendVerification(email);
    if (r.success) toast.success(r.message || 'Verification email sent.');
    else toast.error(r.error);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 bg-[#F7F5F0]">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#2C4A3B] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-2xl">🍄</span>
              </div>
              <h1 className="text-3xl font-extrabold text-[#1A2620] font-['Manrope']">
                Mushroom Farm
              </h1>
            </div>
            <h2 className="text-2xl font-bold text-[#1A2620] font-['Manrope']" data-testid="form-title">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="text-[#5C6B63] mt-2">
              {isLogin
                ? 'Sign in to manage your mushroom farm'
                : 'Register to start your private workspace'}
            </p>
          </div>

          {registerSuccess ? (
            <div className="bg-white border border-[#E4DFD3] rounded-lg p-6 space-y-3" data-testid="register-success">
              <div className="w-12 h-12 bg-[#EDF5EE] rounded-full flex items-center justify-center mb-2">
                <svg className="w-6 h-6 text-[#3A7D44]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <h3 className="text-lg font-bold text-[#1A2620]">Check your email</h3>
              <p className="text-sm text-[#5C6B63]">
                We sent a verification link to <strong>{registerSuccess.email}</strong>. Click the link to activate your account, then come back to log in.
              </p>
              {!registerSuccess.emailSent && (
                <div className="p-3 bg-[#FFF7E5] border border-[#E9D08A] rounded text-xs text-[#8A6A00]">
                  We couldn't deliver the email right now. Use the Resend button below or contact support if the issue persists.
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button onClick={() => handleResend(registerSuccess.email)} className="text-sm text-[#2C4A3B] hover:underline" data-testid="resend-verification-btn">Resend email</button>
                <span className="text-[#5C6B63]">·</span>
                <button onClick={() => { setRegisterSuccess(null); setIsLogin(true); }} className="text-sm text-[#2C4A3B] hover:underline" data-testid="back-to-login-btn">Back to login</button>
              </div>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-[#1A2620] mb-2">Full Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-md border border-[#E4DFD3] bg-white px-4 py-3 text-sm text-[#1A2620] placeholder-[#5C6B63] focus:border-[#2C4A3B] focus:outline-none focus:ring-1 focus:ring-[#2C4A3B]"
                    placeholder="Enter your name"
                    required
                    data-testid="register-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1A2620] mb-2">Mobile Number</label>
                  <input
                    type="tel"
                    inputMode="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full rounded-md border border-[#E4DFD3] bg-white px-4 py-3 text-sm text-[#1A2620] placeholder-[#5C6B63] focus:border-[#2C4A3B] focus:outline-none focus:ring-1 focus:ring-[#2C4A3B]"
                    placeholder="e.g. 9876543210"
                    required
                    pattern="^\+?\d[\d\s-]{6,}$"
                    data-testid="register-phone-input"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-semibold text-[#1A2620] mb-2">Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full rounded-md border border-[#E4DFD3] bg-white px-4 py-3 text-sm text-[#1A2620] placeholder-[#5C6B63] focus:border-[#2C4A3B] focus:outline-none focus:ring-1 focus:ring-[#2C4A3B]"
                placeholder="you@example.com"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1A2620] mb-2">Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full rounded-md border border-[#E4DFD3] bg-white px-4 py-3 text-sm text-[#1A2620] placeholder-[#5C6B63] focus:border-[#2C4A3B] focus:outline-none focus:ring-1 focus:ring-[#2C4A3B]"
                placeholder="Enter password"
                required
                minLength={6}
                data-testid="password-input"
              />
            </div>

            {isLogin && (
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="rememberMe" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="rounded border-[#E4DFD3] text-[#2C4A3B] focus:ring-[#2C4A3B]" data-testid="remember-me-checkbox" />
                  <label htmlFor="rememberMe" className="text-sm text-[#5C6B63]">Remember Me</label>
                </div>
                <Link to="/forgot-password" className="text-sm text-[#2C4A3B] hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
            )}

            {unverifiedEmail && (
              <div className="p-3 bg-[#FFF7E5] border border-[#E9D08A] rounded text-xs text-[#8A6A00] flex items-center justify-between gap-2" data-testid="unverified-banner">
                <span>Email not verified yet.</span>
                <button type="button" onClick={() => handleResend(unverifiedEmail)} className="font-semibold underline" data-testid="resend-verification-inline">Resend link</button>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#2C4A3B] text-white hover:bg-[#3E6652] rounded-md px-4 py-3 font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-button"
            >
              {isSubmitting ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </button>

            {!isLogin && (
              <p className="text-xs text-[#5C6B63] text-center pt-1">
                You'll get your own private workspace. Add staff after signing in.
              </p>
            )}
          </form>
          )}

          {!registerSuccess && (
          <div className="mt-6 text-center">
            <button
              onClick={() => { setIsLogin(!isLogin); setUnverifiedEmail(null); }}
              className="text-sm text-[#2C4A3B] hover:underline"
              data-testid="toggle-form-button"
            >
              {isLogin ? "Don't have an account? Register" : 'Already have an account? Sign In'}
            </button>
          </div>
          )}
        </div>
      </div>

      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1762889278403-1d1a57d9a587?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxtdXNocm9vbSUyMGZhcm0lMjBvcmdhbmljfGVufDB8fHx8MTc3NjE0NDQzNnww&ixlib=rb-4.1.0&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-[#2C4A3B]/40 backdrop-blur-sm"></div>
        <div className="relative h-full flex items-center justify-center p-12">
          <div className="text-white text-center">
            <h2 className="text-4xl font-extrabold mb-4 font-['Manrope']">Track. Analyze. Grow.</h2>
            <p className="text-lg opacity-90">Complete farm management solution for mushroom cultivation</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
